import { useState, useEffect } from "react";
import adminAPI from "./apiClient";
import type { ConversationDetail, Turn, ConversationSession } from "./types";

export interface EnhancedTurn extends Turn {
  action_count: number;
  output_count: number;
}

export interface ConversationDetailState {
  detail: ConversationDetail | null;
  sessions: ConversationSession[];
  turns: EnhancedTurn[];
  loading: boolean;
  error: string | null;
}

/** Count the actions and outputs recorded against each turn. */
function enhanceTurns(data: ConversationDetail): EnhancedTurn[] {
  return data.turns.map((turn) => ({
    ...turn,
    action_count: data.actions.filter((action) => action.turn_id === turn.turn_id).length,
    output_count: data.turn_outputs.filter((output) => output.turn_id === turn.turn_id).length,
  }));
}

/** Load a conversation and its turns from the auditing API. */
export function useConversationDetail(conversationId?: string): ConversationDetailState {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [sessions, setSessions] = useState<ConversationSession[]>([]);
  const [turns, setTurns] = useState<EnhancedTurn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjectDetails = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const data = await adminAPI.getConversationDetail(conversationId);
        setDetail(data);
        setSessions(data.conversations || []);
        setTurns(enhanceTurns(data));
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    void fetchProjectDetails();
  }, [conversationId]);

  return { detail, sessions, turns, loading, error };
}
